import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BUDGET_RANGES } from "../lib/constants";
import { useI18n } from "../context/I18nContext";

const inputClass =
  "w-full bg-transparent border-b border-navy/20 focus:border-gold text-navy placeholder-navy/40 py-3.5 font-sans text-sm outline-none transition-colors duration-300";
const selectClass = inputClass + " text-navy/70";

export const BespokeSearch = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("buyer");
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", category: "", target: "", location: "", budget: "", timeframe: "", financing: "", message: "" });
  const [seller, setSeller] = useState({ name: "", email: "", phone: "", category: "", asset: "", location: "", timeframe: "", message: "" });
  const [loading, setLoading] = useState(false);

  const types = t("opt.searchTypes"); const timeframes = t("opt.timeframes"); const financing = t("opt.financing");
  const arr = (v) => (Array.isArray(v) ? v : []);
  const upB = (k) => (e) => setBuyer((f) => ({ ...f, [k]: e.target.value }));
  const upS = (k) => (e) => setSeller((f) => ({ ...f, [k]: e.target.value }));

  const submitBuyer = async (e) => {
    e.preventDefault();
    if (!buyer.name || !buyer.email) return toast.error(t("toast.nameEmail"));
    setLoading(true);
    try {
      await api.post("/requests", { name: buyer.name, email: buyer.email, phone: buyer.phone, search_type: buyer.category, budget: buyer.budget, message: buyer.message, kind: "buyer_search", extra: { target: buyer.target, location: buyer.location, timeframe: buyer.timeframe, financing: buyer.financing } });
      toast.success(t("toast.buyerOk"));
      setBuyer({ name: "", email: "", phone: "", category: "", target: "", location: "", budget: "", timeframe: "", financing: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.error")); } finally { setLoading(false); }
  };

  const submitSeller = async (e) => {
    e.preventDefault();
    if (!seller.name || !seller.email) return toast.error(t("toast.nameEmail"));
    setLoading(true);
    try {
      await api.post("/requests", { name: seller.name, email: seller.email, phone: seller.phone, search_type: seller.category, message: seller.message, kind: "seller_listing", extra: { asset: seller.asset, location: seller.location, timeframe: seller.timeframe } });
      toast.success(t("toast.sellerOk"));
      setSeller({ name: "", email: "", phone: "", category: "", asset: "", location: "", timeframe: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.error")); } finally { setLoading(false); }
  };

  return (
    <section id="bespoke" data-testid="bespoke-search" className="bg-white py-24 md:py-36 px-6 lg:px-10">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-14">
          <p className="font-sans text-gold text-[11px] tracking-luxury uppercase mb-5">{t("bespoke.label")}</p>
          <h2 className="font-serif text-navy text-4xl md:text-6xl leading-tight">
            {t("bespoke.hA")} <span className="italic text-gold">{t("bespoke.hB")}</span>
          </h2>
          <p className="font-sans text-navy/50 text-sm md:text-base max-w-xl mx-auto mt-6 leading-relaxed">{t("bespoke.sub")}</p>
        </div>

        <div className="flex justify-center mb-14">
          <div className="inline-flex bg-navy/5 rounded-full p-1.5">
            <button data-testid="bespoke-tab-buyer" onClick={() => setMode("buyer")} className={`flex items-center gap-2 px-8 py-3 rounded-full font-sans text-[11px] tracking-wide-lux uppercase transition-all duration-400 ${mode === "buyer" ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}>
              <Search className="w-3.5 h-3.5" /> {t("bespoke.buyer")}
            </button>
            <button data-testid="bespoke-tab-seller" onClick={() => setMode("seller")} className={`flex items-center gap-2 px-8 py-3 rounded-full font-sans text-[11px] tracking-wide-lux uppercase transition-all duration-400 ${mode === "seller" ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}>
              <Tag className="w-3.5 h-3.5" /> {t("bespoke.seller")}
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {mode === "buyer" ? (
            <motion.form key="buyer" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} onSubmit={submitBuyer} data-testid="buyer-form" className="max-w-3xl mx-auto">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                <input className={inputClass} placeholder={t("f.name")} value={buyer.name} onChange={upB("name")} data-testid="buyer-name" />
                <input type="email" className={inputClass} placeholder={t("f.email")} value={buyer.email} onChange={upB("email")} data-testid="buyer-email" />
                <input className={inputClass} placeholder={t("f.phone")} value={buyer.phone} onChange={upB("phone")} />
                <select className={selectClass} value={buyer.category} onChange={upB("category")} data-testid="buyer-category">
                  <option value="">{t("sel.category")}</option>
                  {arr(types).map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
                <input className={inputClass} placeholder={t("buyer.ph.target")} value={buyer.target} onChange={upB("target")} />
                <input className={inputClass} placeholder={t("buyer.ph.location")} value={buyer.location} onChange={upB("location")} />
                <select className={selectClass} value={buyer.budget} onChange={upB("budget")}>
                  <option value="">{t("sel.budget")}</option>
                  {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select className={selectClass} value={buyer.timeframe} onChange={upB("timeframe")}>
                  <option value="">{t("sel.timeframe")}</option>
                  {arr(timeframes).map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                </select>
                <select className={selectClass + " md:col-span-2"} value={buyer.financing} onChange={upB("financing")}>
                  <option value="">{t("sel.financing")}</option>
                  {arr(financing).map((fi) => <option key={fi} value={fi}>{fi}</option>)}
                </select>
                <textarea rows={3} className={inputClass + " md:col-span-2"} placeholder={t("buyer.ph.message")} value={buyer.message} onChange={upB("message")} />
              </div>
              <div className="mt-10 text-center">
                <button type="submit" disabled={loading} data-testid="buyer-submit" className="bg-gold text-navy px-12 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors disabled:opacity-60">
                  {loading ? t("contact.sending") : t("buyer.submit")}
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.div key="seller" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div className="bg-navy grain rounded-2xl p-8 md:p-10 relative overflow-hidden">
                <p className="font-sans text-gold text-[10px] tracking-luxury uppercase mb-5 relative z-10">{t("bespoke.seller")}</p>
                <h3 className="font-serif text-white text-3xl md:text-4xl leading-tight mb-4 relative z-10">{t("seller.hTitle")}</h3>
                <p className="font-sans text-white/70 text-sm leading-relaxed mb-7 relative z-10">{t("seller.hbody")}</p>
                <div className="space-y-4 relative z-10">
                  {[
                    { title: t("seller.listing"), sub: t("seller.listingSub"), price: t("seller.listingPrice"), border: true },
                    { title: t("seller.media"), sub: t("seller.mediaSub"), price: t("seller.mediaPrice"), border: true },
                    { title: t("seller.success"), sub: t("seller.successSub"), price: t("seller.successPrice"), border: false },
                  ].map((r) => (
                    <div key={r.title} className={`flex items-start justify-between gap-4 ${r.border ? "border-b border-white/10 pb-4" : ""}`}>
                      <div>
                        <p className="font-serif text-white text-lg">{r.title}</p>
                        <p className="font-sans text-white/45 text-xs mt-0.5">{r.sub}</p>
                      </div>
                      <span className="font-serif text-gold text-lg whitespace-nowrap">{r.price}</span>
                    </div>
                  ))}
                </div>
                <p className="font-sans text-white/45 text-xs leading-relaxed mt-6 border-t border-white/10 pt-5 relative z-10">{t("seller.mandatory")}</p>
                <button onClick={() => navigate("/pricing")} data-testid="seller-view-fees" className="mt-5 font-sans text-gold text-[11px] tracking-wide-lux uppercase hover:text-gold-light transition-colors relative z-10">
                  {t("seller.viewFees")}
                </button>
              </div>

              <form onSubmit={submitSeller} data-testid="seller-form">
                <h3 className="font-serif text-navy text-3xl mb-8">{t("seller.reqTitle")}</h3>
                <div className="space-y-6">
                  <input className={inputClass} placeholder={t("f.name")} value={seller.name} onChange={upS("name")} data-testid="seller-name" />
                  <input type="email" className={inputClass} placeholder={t("f.email")} value={seller.email} onChange={upS("email")} data-testid="seller-email" />
                  <input className={inputClass} placeholder={t("f.phone")} value={seller.phone} onChange={upS("phone")} />
                  <select className={selectClass} value={seller.category} onChange={upS("category")} data-testid="seller-category">
                    <option value="">{t("sel.assetCategory")}</option>
                    {arr(types).map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                  <input className={inputClass} placeholder={t("seller.ph.asset")} value={seller.asset} onChange={upS("asset")} />
                  <input className={inputClass} placeholder={t("seller.ph.location")} value={seller.location} onChange={upS("location")} />
                  <select className={selectClass} value={seller.timeframe} onChange={upS("timeframe")}>
                    <option value="">{t("sel.desiredTimeframe")}</option>
                    {arr(timeframes).map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                  </select>
                  <textarea rows={3} className={inputClass} placeholder={t("seller.ph.message")} value={seller.message} onChange={upS("message")} />
                </div>
                <button type="submit" disabled={loading} data-testid="seller-submit" className="mt-8 w-full bg-gold text-navy py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-gold-light transition-colors disabled:opacity-60">
                  {loading ? t("contact.sending") : t("seller.submit")}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
